import { AuthEnums, BenefitsEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import { WintSuperkollAPIModelWintSalaryBenefits, WintUtilsWebApi } from "wint-api-client/dist/types";
import { BenefitFormTypes } from "models/formTypes";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { BenefitFrequency, BenefitType } from "models/enums/benefits";
import { IconDefinitionConstants, BenefitConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";

function useBenefitService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getBenefitByIdApi = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).BenefitClient.GetBenefitById(id);
  };

  const createBenefitApi = (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitCreateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).BenefitClient.CreateBenefit(benefit);
  };

  const filter = (filter: WintSuperkollAPIModelWintSalaryBenefits.BenefitFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).BenefitClient.Report(filter);
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter: BenefitFormTypes.Filter) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const updateBenefitApi = (id: string, benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitUpdateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).BenefitClient.UpdateBenefit(id, benefit);
  };

  const createBenefitReApproveApi = (benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitCreateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).BenefitClient.CreateBenefitMutable(
      benefit
    );
  };

  const updateBenefitReApproveApi = (
    id: string,
    benefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitUpdateRequest
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).BenefitClient.UpdateBenefitMutable(
      id,
      benefit
    );
  };

  const reportToAPIFilter = (
    formFilter: BenefitFormTypes.Filter
  ): Omit<WintSuperkollAPIModelWintSalaryBenefits.BenefitFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      benefitTypes: formFilter.benefitTypes?.map((benefitType) => benefitType.id as BenefitType),
      personIds: formFilter.persons?.map((p) => p.id as number),
      start: formFilter.monthRange?.start ? DateHelper.getYearMonth(formFilter.monthRange.start) : undefined,
      end: formFilter.monthRange?.end ? DateHelper.getYearMonth(formFilter.monthRange.end) : undefined,
      groupSelection: formFilter.groupSelection,
    };
  };

  const convertCreateFormToApiPost = (
    form: BenefitFormTypes.BenefitCreate
  ): WintSuperkollAPIModelWintSalaryBenefits.BenefitCreateRequest => {
    const carBenefit: WintSuperkollAPIModelWintSalaryBenefits.BenefitCreateRequest = {
      name: form.name,
      benefitAmount: form.benefitValue ?? 0,
      startPeriod: DateHelper.getYearMonth(form.startDate ?? new Date()),
      endPeriod: form.endDate ? DateHelper.getYearMonth(form.endDate) : undefined,
      frequency: convertFrequecyToApiType(form.frequency),
      benefitType: convertBenefitTypeToApiType(form.benefitType?.id as BenefitType),
      personIds: form.employees?.map((employee) => employee.id as number),
    };

    if (
      form.benefitType?.id === BenefitsEnums.BenefitType.Car ||
      form.benefitType?.id === BenefitsEnums.BenefitType.CarNetDeduction
    ) {
      carBenefit.carBenefitData = {
        modelCode: form.carBenefitData?.modelNumber,
        extraEquipment: form.carBenefitData?.costOfExtraEquipment,
        vehicleTax: form.carBenefitData?.annualTax ?? 0,
        numberOfTenKilometersDrivenInService: form.carBenefitData?.isAnnual10KmMileageMoreThan3000
          ? BenefitConstants.AnnualMileageHighKm
          : BenefitConstants.AnnualMileageLowKm,
        firstDateOfRegistration: form.carBenefitData?.dateOfRegistration,
      };
    }

    return carBenefit;
  };

  const convertFrequecyToApiType = (
    frequency: BenefitFrequency
  ): WintSuperkollAPIModelWintSalaryBenefits.BenefitFrequency => {
    switch (frequency) {
      case BenefitFrequency.Monthly:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitFrequency.Monthly;
      default:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitFrequency.Once;
    }
  };

  const convertApiFrequencyToFormType = (
    frequency: WintSuperkollAPIModelWintSalaryBenefits.BenefitFrequency
  ): BenefitFrequency => {
    switch (frequency) {
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitFrequency.Monthly:
        return BenefitFrequency.Monthly;
      default:
        return BenefitFrequency.Once;
    }
  };

  const convertBenefitTypeToApiType = (
    benefitType: BenefitType
  ): WintSuperkollAPIModelWintSalaryBenefits.BenefitType => {
    switch (benefitType) {
      case BenefitType.Common:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.Common;
      case BenefitType.CarNetDeduction:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.CarNetDeduction;
      case BenefitType.ResidenceSmallNetDeduction:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceSmallNetDeduction;
      case BenefitType.NetDeduction:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeduction;
      case BenefitType.SalarySacrifice:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.SalarySacrifice;
      case BenefitType.Car:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.Car;
      case BenefitType.ResidenceSmallGrossIncrease:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceSmallGrossIncrease;
      case BenefitType.ResidenceNonSmallNetDeduction:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceNonSmallNetDeduction;
      case BenefitType.ResidenceNonSmallGrossIncrease:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceNonSmallGrossIncrease;
      case BenefitType.RentalCompensation:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.RentalCompensation;
      case BenefitType.GrossDeduction:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.GrossDeduction;
      case BenefitType.NetDeductionNoVat:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeductionNoVat;
      case BenefitType.NetDeductionInsurance:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeductionInsurance;
      default:
        return WintSuperkollAPIModelWintSalaryBenefits.BenefitType.Common;
    }
  };

  const convertApiBenefitTypeToFormType = (
    benefitType: WintSuperkollAPIModelWintSalaryBenefits.BenefitType
  ): BenefitType => {
    switch (benefitType) {
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.Common:
        return BenefitType.Common;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.Car:
        return BenefitType.Car;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.CarNetDeduction:
        return BenefitType.CarNetDeduction;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceSmallNetDeduction:
        return BenefitType.ResidenceSmallNetDeduction;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeduction:
        return BenefitType.NetDeduction;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.SalarySacrifice:
        return BenefitType.SalarySacrifice;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceSmallGrossIncrease:
        return BenefitType.ResidenceSmallGrossIncrease;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceNonSmallNetDeduction:
        return BenefitType.ResidenceNonSmallNetDeduction;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.ResidenceNonSmallGrossIncrease:
        return BenefitType.ResidenceNonSmallGrossIncrease;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.RentalCompensation:
        return BenefitType.RentalCompensation;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.GrossDeduction:
        return BenefitType.GrossDeduction;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeductionNoVat:
        return BenefitType.NetDeductionNoVat;
      case WintSuperkollAPIModelWintSalaryBenefits.BenefitType.NetDeductionInsurance:
        return BenefitType.NetDeductionInsurance;
      default:
        return BenefitType.Common;
    }
  };

  const createBenefit = (form: BenefitFormTypes.BenefitCreate) => {
    return createBenefitApi(convertCreateFormToApiPost(form));
  };

  const createBenefitReapprove = (form: BenefitFormTypes.BenefitCreate) => {
    return createBenefitReApproveApi(convertCreateFormToApiPost(form));
  };

  const getBenefitById = (id: string) => {
    return getBenefitByIdApi(id).then((res) => {
      return convertBenefitToForm(res.data);
    });
  };

  const convertBenefitToForm = (
    benefit: WintSuperkollAPIModelWintSalaryBenefits.Benefit
  ): BenefitFormTypes.BenefitUpdate => {
    const benefitType = convertApiBenefitTypeToFormType(benefit.benefitType);
    const isCarBenefit = benefitType === BenefitType.Car || benefitType === BenefitType.CarNetDeduction;
    return {
      name: benefit.name,
      frequency: convertApiFrequencyToFormType(benefit.frequency),
      benefitType: {
        id: benefitType,
        value: LocalizationHelperInstance.getTranslatedEnumValue("Benefit.Type", Number(benefitType)),
      },
      employees: benefit.employeesWithPeriod?.map(
        (employee): AutocompleteListItem => ({
          id: String(employee.personId),
          value: employee.name ?? "",
          icon: IconDefinitionConstants.WUser,
          locked: true,
        })
      ),
      employeeDetails: benefit.employeesWithPeriod?.map((e) => {
        return {
          id: e.personId,
          user: {
            id: e.personId,
            name: e.name ?? "",
            hideProfileImage: false,
            hideUserName: false,
          },
          dateRange: {
            start: DateHelper.getDateFromYearMonth(e.start),
            end: e.end ? DateHelper.getDateFromYearMonth(e.end) : undefined,
          },
        };
      }),
      benefitValue: benefit.benefitAmount,
      modelNumber: isCarBenefit ? benefit.carBenefitData?.modelCode : undefined,
      costOfExtraEquipment: isCarBenefit ? benefit.carBenefitData?.extraEquipment : undefined,
      annualTax: isCarBenefit ? benefit.carBenefitData?.vehicleTax : undefined,
      isAnnual10KmMileageMoreThan3000: isCarBenefit
        ? benefit.carBenefitData?.numberOfTenKilometersDrivenInService &&
          benefit.carBenefitData?.numberOfTenKilometersDrivenInService > BenefitConstants.AnnualMileageThresholdKm
          ? true
          : false
        : undefined,
      dateOfRegistration: isCarBenefit ? benefit.carBenefitData?.firstDateOfRegistration : undefined,
    };
  };

  const convertUpdateFormToApi = (
    form: BenefitFormTypes.BenefitUpdate
  ): WintSuperkollAPIModelWintSalaryBenefits.BenefitUpdateRequest => {
    return {
      name: form.name,
      benefitType: convertBenefitTypeToApiType(form.benefitType?.id as BenefitType),
      benefitAmount: {
        benefitAmount: form.benefitValue ?? 0,
        effectiveFromYearMonth: form.benefitAmountEffectFrom
          ? DateHelper.getYearMonth(form.benefitAmountEffectFrom)
          : undefined,
      },
      employeePeriodUpdates: form.employeeDetails?.map((e) => ({
        personId: e.user.id as number,
        start: DateHelper.getYearMonth(e.dateRange.start!),
        end: e.dateRange?.end ? DateHelper.getYearMonth(e.dateRange.end) : undefined,
      })),
    };
  };

  const updateBenefit = (id: string, form: BenefitFormTypes.BenefitUpdate) => {
    return updateBenefitApi(id, convertUpdateFormToApi(form));
  };

  const updateBenefitReapprove = (id: string, form: BenefitFormTypes.BenefitUpdate) => {
    return updateBenefitReApproveApi(id, convertUpdateFormToApi(form));
  };

  const getYearAndBrandApi = (): Promise<[number[], string[]]> => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .BenefitClient.GetBrandsAndManufacturingYears()
      .then((res) => {
        return [res.data.years ?? [], res.data.brands ?? []];
      });
  };

  const getYearAndBrandLists = (): Promise<[AutocompleteListItem[], AutocompleteListItem[]]> => {
    return getYearAndBrandApi().then(([years, brands]) => {
      return [
        [...years].sort((a, b) => b - a).map((year) => ({ id: year, value: year.toString() })),
        [...brands].sort((a, b) => a.localeCompare(b)).map((brand) => ({ id: brand, value: brand })),
      ];
    });
  };

  const getVehicleListApi = (
    vehicleType: WintSuperkollAPIModelWintSalaryBenefits.VehicleType,
    year: number,
    brand: string
  ): Promise<WintSuperkollAPIModelWintSalaryBenefits.CarModel[]> => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .BenefitClient.GetCarBenefitModels(vehicleType, brand, year)
      .then((res) => {
        return res.data;
      });
  };

  const getBenefitAmount = (
    vehicleTax: number,
    modelNumber: string,
    taxationYear: number,
    extraEquipmentCost?: number,
    isAnnual10KmMileageMoreThan3000?: boolean,
    firstRegistrationDate?: Date
  ): Promise<number> => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .BenefitClient.CalculateCarBenefitAmount(
        vehicleTax,
        modelNumber,
        taxationYear,
        extraEquipmentCost,
        isAnnual10KmMileageMoreThan3000 ? BenefitConstants.AnnualMileageHighKm : BenefitConstants.AnnualMileageLowKm,
        firstRegistrationDate
      )
      .then((res) => {
        return res.data;
      });
  };

  const getVehicleList = (
    vehicleType: BenefitsEnums.CarBenefitSearchMethod,
    year: number,
    brand: string
  ): Promise<AutocompleteListItem[]> => {
    if (vehicleType === BenefitsEnums.CarBenefitSearchMethod.ModelNumber) {
      return Promise.resolve([]);
    }

    const apiVehicleType = convertVehicleSearchMethodToApiType(vehicleType);

    return getVehicleListApi(apiVehicleType, year, brand).then((vehicles) => {
      return vehicles.map((vehicle) => ({
        id: vehicle.modelCode ?? "",
        value: vehicle.modelName ?? "",
        data: vehicle,
      }));
    });
  };

  const convertVehicleSearchMethodToApiType = (
    vehicleType: BenefitsEnums.CarBenefitSearchMethod
  ): WintSuperkollAPIModelWintSalaryBenefits.VehicleType => {
    switch (vehicleType) {
      case BenefitsEnums.CarBenefitSearchMethod.SearchLightTruck:
        return WintSuperkollAPIModelWintSalaryBenefits.VehicleType.LightTruck;
      case BenefitsEnums.CarBenefitSearchMethod.SearchPersonalCar:
      default:
        return WintSuperkollAPIModelWintSalaryBenefits.VehicleType.Car;
    }
  };

  return {
    createBenefit,
    createBenefitReapprove,
    convertCreateFormToApiPost,
    reportFilter,
    getBenefitById,
    updateBenefit,
    updateBenefitReapprove,
    getYearAndBrandLists,
    getVehicleList,
    getBenefitAmount,
  };
}

export default useBenefitService;
